// useAutoSave.js - Auto-save hook with debounce logic

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../../config/api';

const useAutoSave = (activityId, pageNumber, userRole, initialData = {}, part = 'part-b') => {
  const [data, setData] = useState(initialData);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update initial data when it changes
  useEffect(() => {
    setData(initialData);
  }, [JSON.stringify(initialData)]);

  const saveData = useCallback(async (dataToSave) => {
    if (!activityId || !isMountedRef.current) return;

    try {
      setSaveStatus('saving');

      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      // Construct endpoint based on part (part-b or part-c)
      const endpoint = userRole === 'teacher'
        ? `/secondary/teacher/domain-activities/${activityId}/${part}/page/${pageNumber}/teacher-fields`
        : `/secondary/user/domain-activities/${activityId}/${part}/page/${pageNumber}/student-fields`;

      await axios.put(
        `${apiUrl}${endpoint}`,
        dataToSave,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (isMountedRef.current) {
        setSaveStatus('saved');
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      if (isMountedRef.current) {
        setSaveStatus('error');
      }
    }
  }, [activityId, pageNumber, userRole, part]);

  const updateData = useCallback((updates) => {
    setData(prevData => {
      const newData = { ...prevData, ...updates };

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for auto-save (2 seconds)
      timeoutRef.current = setTimeout(() => {
        saveData(newData);
      }, 2000);

      return newData;
    });
  }, [saveData]);

  const saveNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    saveData(data);
  }, [data, saveData]);

  return {
    data,
    updateData,
    saveNow,
    saveStatus,
    lastSaved
  };
};

export default useAutoSave;
